import EventBus from '../services/service.eventBus';
import { state as aftermarketState } from '../store/store.aftermarket';
import {
  state as answerState,
  clearFromLocal as clearAnswersFromLocal,
  populateFromLocal as populateAnswersFromLocal,
  reset as resetAnswers,
  setInitialized as setAnswersInitialized,
} from '../store/store.answers';
import { state as exteriorState } from '../store/store.exterior';
import { state as interiorState } from '../store/store.interior';
import { state as mechanicalState } from '../store/store.mechanical';
import { state as supplementState } from '../store/store.supplement';
import { clearFromLocal as clearASCTrackingFromLocal } from '../store/store.tracking-asc';
import { state as vehicleConditionsState } from '../store/store.vehicle-conditions';
import { trackFormEngagement } from '../tracking/tracking.asc';
import { findAnswerGroup, getAnsweredQuestionCount, hasAnsweredQuestions, multiselectHasAnsweredQuestions } from '../utils/utils.answers';
import { DISPOSAL_TYPES } from '../utils/utils.offer';
import {
  QUESTION_DISPOSAL_TYPE,
  QUESTION_TOGGLE_MODIFICATIONS,
  QUESTION_TWO_KEYS,
  answersArrayToMap,
  extractAllByType,
  toggleModificationsQuestion,
  twoKeysStaticQuestion,
} from '../utils/utils.question';
import { combineOptions, filterFalseOptions, filterTrueOptions, trackUserInteraction } from '../utils/utils.vehicle';

export const loadAnswersFromBrowser = () => {
  populateAnswersFromLocal();
  setAnswersInitialized(true);
  updateBadgeCounters(exteriorState, interiorState, mechanicalState, answerState.conditions);
  setIsConditionPerfect();
  onToggleModificationsAnswered();
};

export const setModificationQuestionVisible = () => {
  aftermarketState.showModificationsQuestionGroup = true;
  aftermarketState.showModificationsQuestion = true;
  aftermarketState.showModificationRemovalPrompt = false;
};

export const setConsumerOffer = (isConsumerOffer: boolean) => {
  answerState.isConsumerOffer = isConsumerOffer;
};

export const loadAnswersFromQuestions = ({ conditions, history, replacement, other }) => {
  answerState.history = answersArrayToMap(history, true);
  answerState.conditions = extractAllByType(conditions, true);
  answerState.replacementVehicle = extractAllByType(replacement);
  answerState.other = answersArrayToMap(other);

  // Inject default answers for these questions. Ideally we'd rework the question data to allow default values and pass in that way instead.
  const yearRangeMin = supplementState.yearRange.values.find((r) => r.id === 'yearRange/min')?.label;
  const yearRangeMax = supplementState.yearRange.values.find((r) => r.id === 'yearRange/max')?.label;
  onQuestionAnswered('qrv/YearRange', `${yearRangeMin},${yearRangeMax}`);
  setAnswersInitialized(true);

  EventBus.publish('AnswersLoaded', {
    answers: {
      conditions: answerState.conditions,
      history: answerState.history,
      contactInfo: answerState.contactInfo,
      replacementVehicle: answerState.replacementVehicle,
      vehicle: answerState.vehicle,
    },
  });
};

export const onQuestionAnswered = (answerKey: string, value: string | number | boolean, comment: string = '') => {
  if (!answerKey) {
    console.error('answerKey is required');
    return;
  }
  const group = findAnswerGroup(answerKey, answerState);
  if (group) {
    const { conditions, history, replacementVehicle, contactInfo, other } = answerState;
    // update if applicable
    trackFormEngagement(group, answerKey, value);
    const newAnswer = answerState[group][answerKey];
    newAnswer.value = value;
    newAnswer.comment = comment;
    answerState[group] = { ...answerState[group], [answerKey]: newAnswer };

    // update badge counters for condition page
    updateBadgeCounters(exteriorState, interiorState, mechanicalState, conditions);

    setIsConditionPerfect();

    // toggleModifications
    onToggleModificationsAnswered();
    // TwoKeysMods Interaction Status
    onTwoKeysModsInteraction();

    // Notify the event bus
    EventBus.publish('QuestionAnswered', {
      answered: { [answerKey]: value, section: group },
      contactInfo: {
        firstName: contactInfo.firstName,
        lastName: contactInfo.lastName,
        email: contactInfo.email,
        phone: contactInfo.phone,
        zip: contactInfo.zip,
      },
      vehicle: {
        transmissionId: answerState.vehicle.transmissionId,
        engineId: answerState.vehicle.engineId,
        drivetrainId: answerState.vehicle.drivetrainId,
        colorId: answerState.vehicle.colorId,
      },
      conditions,
      history,
      replacementVehicle,
      other,
    });
  } else {
    console.error('Unable to update answer', { answerKey, value, comments: comment }, answerState.contactInfo);
  }
};

export const onStateFieldAnswered = (canonicalName: string = '', answerKey: string = '', value: boolean | string | string[] | number[] | number = '') => {
  // update if applicable
  answerState[canonicalName] = { ...answerState[canonicalName], [answerKey]: value };
  const { conditions, history, replacementVehicle, contactInfo, vehicle } = answerState;
  trackFormEngagement(canonicalName, answerKey, value);

  // Notify the event bus
  EventBus.publish('QuestionAnswered', {
    answered: { [answerKey]: value, section: canonicalName },

    contactInfo: {
      firstName: contactInfo.firstName,
      lastName: contactInfo.lastName,
      email: contactInfo.email,
      phone: contactInfo.phone,
      zip: contactInfo.zip,
    },
    vehicle: {
      transmissionId: vehicle.transmissionId,
      engineId: vehicle.engineId,
      drivetrainId: vehicle.drivetrainId,
      colorId: vehicle.colorId,
    },
    conditions,
    history,
    replacementVehicle,
  });
};

export const onVehicleOptionsAnswered = (canonicalName: string = '', answerKey: string = '', value) => {
  const gatherOptions = combineOptions(answerState.vehicle.apiSelectedOptionIds, answerState.vehicle.apiUnselectedOptionIds);
  const updateOptions = gatherOptions.map((option) => {
    return option.optionId === value.optionId ? { ...option, isSelected: value.isSelected } : option;
  });

  const optionsTrue = updateOptions.filter((options) => options.isSelected === true).map((options) => options.optionId);
  const optionsFalse = updateOptions.filter((options) => options.isSelected === false).map((options) => options.optionId);
  answerState[canonicalName] = { ...answerState[canonicalName], apiSelectedOptionIds: optionsTrue };
  answerState[canonicalName] = { ...answerState[canonicalName], apiUnselectedOptionIds: optionsFalse };

  const { conditions, history, replacementVehicle, contactInfo, vehicle } = answerState;

  // Notify the event bus
  EventBus.publish('QuestionAnswered', {
    answered: { [answerKey]: value, section: canonicalName },
    contactInfo: {
      firstName: contactInfo.firstName,
      lastName: contactInfo.lastName,
      email: contactInfo.email,
      phone: contactInfo.phone,
      zip: contactInfo.zip,
    },
    vehicle: {
      transmissionId: vehicle.transmissionId,
      engineId: vehicle.engineId,
      drivetrainId: vehicle.drivetrainId,
      colorId: vehicle.colorId,
    },
    conditions,
    history,
    replacementVehicle,
  });
};

export const setAnswersFromVehicle = (vehConfigState, dealerInfoAnswers = null) => {
  answerState.vehicle.vin = vehConfigState.vin;
  answerState.vehicle.yearId = vehConfigState.yearId;
  answerState.vehicle.make = vehConfigState.make;
  answerState.vehicle.makeId = vehConfigState.makeId;
  answerState.vehicle.model = vehConfigState.model;
  answerState.vehicle.modelId = vehConfigState.modelId;
  answerState.vehicle.trim = vehConfigState.trim;
  answerState.vehicle.trimId = vehConfigState.trimId;
  answerState.vehicle.drivetrainId = getSelectedTED(vehConfigState.availDrivetrains).optionId;
  answerState.vehicle.engineId = getSelectedTED(vehConfigState.availEngines).optionId;
  answerState.vehicle.transmissionId = getSelectedTED(vehConfigState.availTransmissions).optionId;
  answerState.vehicle.colorId = getSelectedTED(vehConfigState.availColors).optionId;

  if (vehConfigState.availOptions[0].optionId !== 0) {
    answerState.vehicle.apiSelectedOptionIds = filterTrueOptions(vehConfigState.availOptions);
    answerState.vehicle.apiUnselectedOptionIds = filterFalseOptions(vehConfigState.availOptions);
  }

  if (dealerInfoAnswers?.dealerId) {
    answerState.dealerInfo = dealerInfoAnswers;
  }

  // Notify the event bus
  EventBus.publish('QuestionAnswered', {
    answers: {
      vehicle: {
        transmissionId: answerState.vehicle.transmissionId,
        engineId: answerState.vehicle.engineId,
        drivetrainId: answerState.vehicle.drivetrainId,
        colorId: answerState.vehicle.colorId,
      },
      conditions: answerState.conditions,
      history: answerState.history,
      contactInfo: answerState.contactInfo,
      replacementVehicle: answerState.replacementVehicle,
      dealerInfo: answerState.dealerInfo,
    },
  });
};
export const setAnswersFromDealer = (dealerInfo) => {
  answerState.dealerInfo = { ...answerState.dealerInfo, ...dealerInfo };
};
const getSelectedTED = (vehicleTed) => {
  if (vehicleTed.length === 1) {
    return vehicleTed[0];
  }
  if (vehicleTed.filter((ted) => ted.isSelected === true).length > 0) {
    return vehicleTed.filter((ted) => ted.isSelected === true)[0];
  }
  return {
    optionId: 0,
    displayName: '',
  };
};

export const onVehicleValidVinSet = (vin) => {
  onStateFieldAnswered('vehicle', 'isValid', true);
  onStateFieldAnswered('vehicle', 'vin', vin);
};

export const onVehicleVinClear = () => {
  onStateFieldAnswered('vehicle', 'isValid', false);
  onStateFieldAnswered('vehicle', 'vin', '');
};

export const onVehicleSet = ({ yearId, year, makeId, make, modelId, model, trimId, trim }) => {
  onStateFieldAnswered('vehicle', 'makeId', makeId);
  onStateFieldAnswered('vehicle', 'make', make);
  onStateFieldAnswered('vehicle', 'modelId', modelId);
  onStateFieldAnswered('vehicle', 'model', model);
  onStateFieldAnswered('vehicle', 'trimId', trimId);
  onStateFieldAnswered('vehicle', 'trim', trim);
  onStateFieldAnswered('vehicle', 'yearId', yearId);
  onStateFieldAnswered('vehicle', 'year', year);
};

export const onVehicleConfigSet = ({ color, colorId, drivetrain, drivetrainId, engine, engineId, transmission, transmissionId }) => {
  onStateFieldAnswered('vehicle', 'colorId', colorId);
  onStateFieldAnswered('vehicle', 'color', color);
  onStateFieldAnswered('vehicle', 'drivetrainId', drivetrainId);
  onStateFieldAnswered('vehicle', 'drivetrain', drivetrain);
  onStateFieldAnswered('vehicle', 'engineId', engineId);
  onStateFieldAnswered('vehicle', 'engine', engine);
  onStateFieldAnswered('vehicle', 'transmissionId', transmissionId);
  onStateFieldAnswered('vehicle', 'transmission', transmission);
};

export const onToggleModificationsAnswered = () => {
  const answerConditions = answerState['conditions'];
  aftermarketState.showModificationsQuestionGroup = Boolean(answerConditions[QUESTION_TOGGLE_MODIFICATIONS].value);
  aftermarketState.showModificationsQuestion = answerState['history'][QUESTION_TWO_KEYS].value !== '';

  aftermarketState.showModificationRemovalPrompt = !!(!Boolean(answerConditions[QUESTION_TOGGLE_MODIFICATIONS].value) && isModificationQuestionsAnswered());
};

export const isModificationQuestionsAnswered = () => {
  const aftermarketIds = aftermarketState.questionsAftermarket.map((q) => q.questionId);
  const allConditions = answerState['conditions'];
  const answerConditionIds = Object.keys(allConditions); // answerConditions.map(answerId => answerId);
  // intersection of answered conditions and after market id's
  const answeredAfterMarkets = answerConditionIds.filter((a) => aftermarketIds.indexOf(a));
  const isAnswered = answeredAfterMarkets.some((a) => {
    const hasComment = allConditions[a].comment !== '';
    const isAnswered = a !== 'toggleQuestionModifications1' && allConditions[a].value !== '' && allConditions[a].value !== false;
    return isAnswered || hasComment;
  });

  return isAnswered;
};
// reset all aftermarket questions to their initial values
export const removeModificationsAnswers = () => {
  const answerConditions = answerState['conditions'];
  // set mods question to 'no'
  answerConditions[QUESTION_TOGGLE_MODIFICATIONS].value = false;
  const resetAnswersAfterMarket = extractAllByType(aftermarketState.questionsAftermarket, true);
  const aftermarketIds = Object.keys(resetAnswersAfterMarket);

  Object.keys(answerConditions).forEach((answerQuestionID) => {
    aftermarketIds.forEach((afterMarketId) => {
      if (answerQuestionID === afterMarketId) {
        const newAnswer = answerState['conditions'][answerQuestionID];
        newAnswer.value = resetAnswersAfterMarket[answerQuestionID].value;
        newAnswer.comment = resetAnswersAfterMarket[answerQuestionID].comment;
        /*  Update stored questions*/
        answerState['conditions'] = { ...answerState['conditions'], [answerQuestionID]: newAnswer };
      }
    });
  });

  onTwoKeysModsInteraction();
};

export function onTwoKeysModsInteraction() {
  const categoriesQuestionsAnswers = {
    twokeys: {
      questions: [twoKeysStaticQuestion],
      answers: answerState.history,
    },
    mods: {
      questions: [toggleModificationsQuestion],
      answers: answerState.conditions,
    },
    stereo: {
      questions: aftermarketState.stereoQuestions,
      answers: answerState.conditions,
    },
    wheelstires: {
      questions: aftermarketState.wheelsTiresQuestions,
      answers: answerState.conditions,
    },
    suspension: {
      questions: aftermarketState.suspensionQuestions,
      answers: answerState.conditions,
    },
    performance: {
      questions: aftermarketState.performanceQuestions,
      answers: answerState.conditions,
    },
    exhaust: {
      questions: aftermarketState.exhaustQuestions,
      answers: answerState.conditions,
    },
  };

  const userInteraction = trackUserInteraction(categoriesQuestionsAnswers);

  aftermarketState.userInteraction = {
    ...aftermarketState.userInteraction,
    ...userInteraction,
  };

  EventBus.publish('TwoKeysModsUserInteraction', aftermarketState.userInteraction);
}

export const clearAnswers = () => {
  clearAnswersFromLocal();
  clearASCTrackingFromLocal();
};

export const updateBadgeCounters = (exteriorState, interiorState, mechanicalState, answers = []) => {
  let tabFront = 0;
  let tabDriver = 0;
  let tabPassenger = 0;
  let tabRear = 0;
  let tabWindowsLight = 0;
  let tabInteriorSch = 0;
  let tabInteriorDbl = 0;

  // front
  tabFront += hasAnsweredQuestions(exteriorState.questionsFrontBumper, answers);
  tabFront += hasAnsweredQuestions(exteriorState.questionsFrontMoldingTrim, answers);
  tabFront += hasAnsweredQuestions(exteriorState.questionsGrille, answers);
  tabFront += hasAnsweredQuestions(exteriorState.questionsHood, answers);

  // driver & passenger
  tabDriver += hasAnsweredQuestions(exteriorState.questionsDriverFrontFender, answers);
  tabDriver += hasAnsweredQuestions(exteriorState.questionsDriverFrontDoor, answers);
  tabDriver += hasAnsweredQuestions(exteriorState.questionsDriverMoldingTrim, answers);
  tabDriver += hasAnsweredQuestions(exteriorState.questionsDriverRearDoor, answers);
  tabDriver += hasAnsweredQuestions(exteriorState.questionsDriverRearPanel, answers);
  tabDriver += hasAnsweredQuestions(exteriorState.questionsDriverRockerPanel, answers);
  tabDriver += hasAnsweredQuestions(exteriorState.questionsTiresRims, answers);

  tabPassenger += hasAnsweredQuestions(exteriorState.questionsPassengerFrontFender, answers);
  tabPassenger += hasAnsweredQuestions(exteriorState.questionsPassengerFrontDoor, answers);
  tabPassenger += hasAnsweredQuestions(exteriorState.questionsPassengerRearDoor, answers);
  tabPassenger += hasAnsweredQuestions(exteriorState.questionsPassengerRearPanel, answers);
  tabPassenger += hasAnsweredQuestions(exteriorState.questionsPassengerRockerPanel, answers);
  tabPassenger += hasAnsweredQuestions(exteriorState.questionsTiresRims, answers);
  tabPassenger += hasAnsweredQuestions(exteriorState.questionsPassengerMoldingTrim, answers);

  /* breaks*/
  tabDriver += hasAnsweredQuestions(exteriorState.questionsBrakes, answers) || multiselectHasAnsweredQuestions(exteriorState.questionsBrakes, answers);
  tabPassenger += hasAnsweredQuestions(exteriorState.questionsBrakes, answers) || multiselectHasAnsweredQuestions(exteriorState.questionsBrakes, answers);

  // rear
  tabRear += hasAnsweredQuestions(exteriorState.questionsRearBumper, answers);
  tabRear += hasAnsweredQuestions(exteriorState.questionsRoof, answers);
  tabRear += hasAnsweredQuestions(exteriorState.questionsTrunk, answers);
  tabRear += hasAnsweredQuestions(exteriorState.questionsRearMoldingTrim, answers);

  // windows lights
  tabWindowsLight += hasAnsweredQuestions(exteriorState.questionsHeadlights, answers);
  tabWindowsLight += hasAnsweredQuestions(exteriorState.questionsRearWindow, answers) || multiselectHasAnsweredQuestions(exteriorState.questionsRearWindow, answers);
  tabWindowsLight += hasAnsweredQuestions(exteriorState.questionsSideWindows, answers);
  tabWindowsLight += hasAnsweredQuestions(exteriorState.questionsTaillights, answers);
  tabWindowsLight += hasAnsweredQuestions(exteriorState.questionsWindshield, answers) || multiselectHasAnsweredQuestions(exteriorState.questionsWindshield, answers);

  tabWindowsLight += multiselectHasAnsweredQuestions(exteriorState.questionsMirrors, answers);
  tabWindowsLight += multiselectHasAnsweredQuestions(exteriorState.questionsSideWindows, answers);
  tabWindowsLight += hasAnsweredQuestions(exteriorState.questionsDashboardLights, answers);
  tabWindowsLight += hasAnsweredQuestions(exteriorState.questionsRearWindow);
  // internal
  tabInteriorSch += hasAnsweredQuestions(interiorState.questionsDashboard, answers);
  tabInteriorSch += hasAnsweredQuestions(interiorState.questionsFrontSeats, answers);
  tabInteriorSch += hasAnsweredQuestions(interiorState.questionsRearSeats, answers);
  tabInteriorSch += hasAnsweredQuestions(interiorState.questionsThirdRowSeats, answers);
  tabInteriorSch += hasAnsweredQuestions(interiorState.questionsCargoBed, answers);
  tabInteriorSch += hasAnsweredQuestions(interiorState.questionsHeadliner, answers);
  tabInteriorSch += hasAnsweredQuestions(interiorState.questionsCarpet, answers);
  tabInteriorSch += hasAnsweredQuestions(interiorState.questionsMoldingTrim, answers);
  tabInteriorSch += hasAnsweredQuestions(interiorState.questionsOdors, answers);

  tabInteriorDbl += getAnsweredQuestionCount(interiorState.questionsDashboardLights, answers);

  mechanicalState.subIssueCounts = [
    multiselectHasAnsweredQuestions(mechanicalState.questionsEngine, answers),
    multiselectHasAnsweredQuestions(mechanicalState.questionsTransmission, answers),
    multiselectHasAnsweredQuestions(mechanicalState.questionsSteering, answers),
    hasAnsweredQuestions(mechanicalState.questionsExhaust, answers),
    hasAnsweredQuestions(mechanicalState.questionsSuspension, answers),
    multiselectHasAnsweredQuestions(mechanicalState.questionsHeatingAir, answers),
    multiselectHasAnsweredQuestions(mechanicalState.questionsBatteryElectrical, answers),
    multiselectHasAnsweredQuestions(mechanicalState.questionsStereo, answers),
    multiselectHasAnsweredQuestions(mechanicalState.questionsOdometer, answers),
    multiselectHasAnsweredQuestions(mechanicalState.questionsSunMoonRoof, answers),
    hasAnsweredQuestions(mechanicalState.questionsAirbags, answers),
    multiselectHasAnsweredQuestions(mechanicalState.questionsFrameStructural, answers),
  ];

  exteriorState.conditionPageFrontBadge = tabFront;
  exteriorState.conditionPageDriverBadge = tabDriver;
  exteriorState.conditionPagePassengerBadge = tabPassenger;
  exteriorState.conditionPageRearBadge = tabRear;
  exteriorState.conditionPageWindowsLightsBadge = tabWindowsLight;

  interiorState.conditionPageSche = tabInteriorSch;
  interiorState.conditionPageDbl = tabInteriorDbl;

  mechanicalState.issueCount = mechanicalState.subIssueCounts.reduce((s1, s2) => s1 + s2, 0);
};

export const resetStateAnswers = () => {
  resetAnswers();
};

export const clearCaptcha = () => {
  answerState.captcha.response = '';
};
export const setResponseCaptcha = (value) => {
  answerState.captcha.response = value;
};
export const setCaptchaReady = (value) => {
  answerState.captcha.captchaReady = value;
};

export const setAnswerForDisposalType = () => {
  if (answerState.disposalOptions.isKelleyBuysYourCar && answerState.disposalOptions.isInstantCashOffer) {
    if (answerState.disposalOptions.isKelleyBuysYourCarFakeDoor) {
      onQuestionAnswered(QUESTION_DISPOSAL_TYPE, DISPOSAL_TYPES.ICOANDKBYCFAKE);
    } else {
      onQuestionAnswered(QUESTION_DISPOSAL_TYPE, DISPOSAL_TYPES.ICOANDKBYC);
    }
  } else if (answerState.disposalOptions.isKelleyBuysYourCar) {
    onQuestionAnswered(QUESTION_DISPOSAL_TYPE, DISPOSAL_TYPES.KBYC);
  } else {
    onQuestionAnswered(QUESTION_DISPOSAL_TYPE, DISPOSAL_TYPES.ICO);
  }
};

const setIsConditionPerfect = () => {
  const isPerfect =
    exteriorState.conditionPageFrontBadge +
      exteriorState.conditionPageDriverBadge +
      exteriorState.conditionPagePassengerBadge +
      exteriorState.conditionPageRearBadge +
      exteriorState.conditionPageWindowsLightsBadge +
      interiorState.conditionPageSche +
      interiorState.conditionPageDbl +
      mechanicalState.issueCount ===
    0;

  vehicleConditionsState.isConditionPerfect = isPerfect;
};

const actions = {
  onQuestionAnswered,
  loadAnswersFromQuestions,
  loadAnswersFromBrowser,
  clearAnswers,
  onVehicleSet,
  onVehicleValidVinSet,
  onVehicleVinClear,
  resetStateAnswers,
  setAnswersFromDealer,
  setConsumerOffer,
  setAnswerForDisposalType,
};

export { actions };
export default actions;
